import { GRANT_AMOUNT_MAX } from '@developerz/domain';
import { createSignal, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { DEFAULT_INVITE_BUNDLE, type InviteAccessRequestInput } from '../lib/admin-access-requests';
import { AdminGrantDuration } from './AdminGrantDuration';
import { Field } from './Field';

// The provision form inside `<AdminInviteDialog/>`: the seats, how long the grant
// lasts (or ongoing, plan 102 slice 05) and an optional reason, PREFILLED from
// `DEFAULT_INVITE_BUNDLE`. Mounted
// KEYED on the application it is for (the dialog's `<Show keyed>`), so every
// open starts from the bundle — a draft edited for one applicant never leaks
// into the next.
//
// THE BOX BUDGET IS TEXT, NOT AN INPUT. It is derived from the seats server-side
// (`starterBoxBudget`, `apps/api/src/deps/invitations.ts`, one call into
// `enrollmentBoxBudget`) and `boxes` is not a grant a person can hold. This
// bundle cannot import `@developerz/billing` (`lib/billing.ts`), and restating
// the rule here would be the second copy `scripts/one-impl.test.ts` exists to
// refuse — so the line says HOW the budget follows the seats, and the NUMBER is
// the server's `bundle.boxes`, shown by `<AdminInviteDialog/>` once it answers.
//
// NO CLIENT VALIDATION beyond what the inputs' own `min`/`max` express: the
// body is `AccessRequestInviteSchema` and a refusal names the field; the
// server's `detail` is rendered verbatim, right here under the fields.

/** The box-budget line is a second hint under the seats, one the Field does not
 * render, so the seats input names it beside the Field's own — first, as it
 * paints above that one. */
const BUDGET_HINT_ID = 'invite-ai-devs-budget';

export function AdminInviteForm(props: {
  readonly busy: boolean;
  readonly error: string | null;
  readonly onSend: (body: InviteAccessRequestInput) => void;
}): JSX.Element {
  const [aiDevs, setAiDevs] = createSignal(String(DEFAULT_INVITE_BUNDLE.aiDevs));
  const [expires, setExpires] = createSignal(String(DEFAULT_INVITE_BUNDLE.expiresInDays));
  const [ongoing, setOngoing] = createSignal(false);
  const [reason, setReason] = createSignal('');

  const send = (): void => {
    if (props.busy) return;
    const why = reason().trim();
    props.onSend({
      ai_devs: Number(aiDevs()),
      expires_in_days: ongoing() ? null : Number(expires()),
      ...(why === '' ? {} : { reason: why }),
    });
  };

  return (
    // A <form> with implicit submit, not a bare div: Enter in a number field
    // means "send" to an operator, and the `busy` gate inside `send` is what
    // stops a double submit — `disabled` is a rendering, not a lock.
    <form
      id="admin-invite-form"
      class="field-group"
      onSubmit={(e) => {
        e.preventDefault();
        send();
      }}
    >
      <Field
        label={t('admin.access_field_ai_devs')}
        id="invite-ai-devs"
        desc={t('admin.access_field_ai_devs_desc', { max: GRANT_AMOUNT_MAX })}
      >
        {(a11y) => (
          <>
            <input
              {...a11y}
              aria-describedby={[BUDGET_HINT_ID, a11y['aria-describedby']]
                .filter(Boolean)
                .join(' ')}
              class="input"
              type="number"
              min="1"
              max={GRANT_AMOUNT_MAX}
              step="1"
              disabled={props.busy}
              value={aiDevs()}
              onInput={(e) => setAiDevs(e.currentTarget.value)}
            />
            <p id={BUDGET_HINT_ID} class="field__desc" data-testid="invite-box-budget">
              {t('admin.access_field_boxes_derived')}
            </p>
          </>
        )}
      </Field>
      <AdminGrantDuration
        id="invite"
        busy={props.busy}
        days={expires()}
        ongoing={ongoing()}
        onDays={setExpires}
        onOngoing={setOngoing}
      />
      <Field
        label={t('admin.access_field_reason')}
        id="invite-reason"
        desc={t('admin.access_field_reason_desc')}
      >
        {(a11y) => (
          <textarea
            {...a11y}
            class="textarea"
            rows="2"
            maxlength="500"
            disabled={props.busy}
            value={reason()}
            onInput={(e) => setReason(e.currentTarget.value)}
          />
        )}
      </Field>
      <Show when={props.error}>
        {(msg) => (
          <p class="field__error" role="alert">
            {msg()}
          </p>
        )}
      </Show>
    </form>
  );
}
